import React, { FunctionComponent, SVGProps, useMemo } from 'react';
import styles from './CardFace.module.css';
import { twMerge } from 'tailwind-merge';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
// Import SVG components for suits
import { ReactComponent as Clubs } from '@/assets/decks/card-suits/clubs.svg';
import { ReactComponent as Diamonds } from '@/assets/decks/card-suits/diamonds.svg';
import { ReactComponent as Hearts } from '@/assets/decks/card-suits/hearts.svg';
import { ReactComponent as Spades } from '@/assets/decks/card-suits/spades.svg';
// Import SVG components for ranks
import { ReactComponent as Ace } from '@/assets/decks/card-symbol/A.svg';
import { ReactComponent as Two } from '@/assets/decks/card-symbol/2.svg';
import { ReactComponent as Three } from '@/assets/decks/card-symbol/3.svg';
import { ReactComponent as Four } from '@/assets/decks/card-symbol/4.svg';
import { ReactComponent as Five } from '@/assets/decks/card-symbol/5.svg';
import { ReactComponent as Six } from '@/assets/decks/card-symbol/6.svg';
import { ReactComponent as Seven } from '@/assets/decks/card-symbol/7.svg';
import { ReactComponent as Eight } from '@/assets/decks/card-symbol/8.svg';
import { ReactComponent as Nine } from '@/assets/decks/card-symbol/9.svg';
import { ReactComponent as Ten } from '@/assets/decks/card-symbol/10.svg';
import { ReactComponent as Jack } from '@/assets/decks/card-symbol/J.svg';
import { ReactComponent as Queen } from '@/assets/decks/card-symbol/Q.svg';
import { ReactComponent as King } from '@/assets/decks/card-symbol/K.svg';
import { ReactComponent as CardBack } from '@/assets/decks/card-back.svg';
import { DeckDesign } from '@/assets/decks/DeckDesign';
import { useCardFaceStyles } from './useCardFaceStyles';

type SVGComponent = FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined }>;

const rankComponents: Record<string, SVGComponent> = {
  a: Ace,
  '2': Two,
  '3': Three,
  '4': Four,
  '5': Five,
  '6': Six,
  '7': Seven,
  '8': Eight,
  '9': Nine,
  t: Ten,
  j: Jack,
  q: Queen,
  k: King,
  '?': Ace,
};

const suitComponents: Record<string, SVGComponent> = {
  '?': Clubs,
  c: Clubs,
  d: Diamonds,
  h: Hearts,
  s: Spades,
  clubs: Clubs,
  diamonds: Diamonds,
  hearts: Hearts,
  spades: Spades,
};

export enum CardSizes {
  xxs = 'xxs',
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  xxl = 'xxl',
}

export const overlapMargins = {
  [CardSizes.xxs]: '-ml-3',
  [CardSizes.xs]: '-ml-4',
  [CardSizes.sm]: '-ml-7',
  [CardSizes.md]: '-ml-9',
  [CardSizes.lg]: '-ml-12',
  [CardSizes.xl]: '-ml-[3.25em]',
  [CardSizes.xxl]: '-ml-[3.25em]',
};

interface HandleCard {
  rank: string | undefined;
  suit: string | undefined;
  highlight?: boolean;
  isOverlap?: boolean;
  isElevated?: boolean;
  isWinner?: boolean;
  isButtonCard?: boolean;
  isDiscarded?: boolean;
  onClick?: () => void;
  className?: string;
  handCardIndex?: number;
  size?: CardSizes;
  cardAreFanned?: boolean;
  totalCards?: number;
}

export function getSizeClasses(size: CardSizes): string {
  return twMerge(
    size === CardSizes.xxs && 'w-5 rounded-[0.1041666667em]',
    size === CardSizes.xs && 'w-7 rounded-[0.1458333333em]',
    size === CardSizes.sm && 'w-10 rounded-[0.2083333333em]',
    size === CardSizes.md && 'w-15 rounded-[0.3125em]',
    size === CardSizes.lg && 'w-20 rounded-[0.4166666667em]',
    size === CardSizes.xl && 'w-22 rounded-[0.4583333333em]',
    size === CardSizes.xxl && 'w-28 rounded-[0.5833333333em]',
  );
}
const CardFace: React.FC<HandleCard> = ({
  rank = '?',
  suit = '?',
  highlight = false,
  isOverlap = false,
  isElevated = false,
  isWinner = false,
  isButtonCard = false,
  isDiscarded = false,
  onClick,
  className = '',
  handCardIndex = 0,
  size = CardSizes.lg,
  cardAreFanned = false,
  totalCards = 2,
}) => {
  const user = useSelector((state: ReducerState) => state.auth);
  const RankSvg = rankComponents[rank.toLowerCase()];
  const SuitSvg = suitComponents[suit.toLowerCase()];

  const { angle, translateY } = useMemo(() => {
    const maxAngle = totalCards * 3; // Maximum angle for fanning
    const step = (maxAngle * 2) / (totalCards - 1);
    const angle = -maxAngle + step * handCardIndex;

    const middleIndex = (totalCards - 1) / 2;
    const distanceFromMiddle = handCardIndex - middleIndex;
    const translateY =
      handCardIndex >= middleIndex
        ? distanceFromMiddle ** 2 / totalCards + distanceFromMiddle / 6
        : distanceFromMiddle ** 2 / totalCards + distanceFromMiddle / 5; // Adjust the multiplier as needed
    return { angle, translateY };
  }, [handCardIndex, totalCards]);

  const isCardBack = useMemo(() => {
    return rank === '?' || suit === '?';
  }, [rank, suit]);

  const {
    suitColorClass,
    backgroundColorClass,
    symbolLayoutClasses,
    bigSuitClasses,
    smallSuitClasses,
    symbolSizeClasses,
    bigSuitSizeClasses,
    smallSuitSizeClasses,
  } = useCardFaceStyles(size, suit, user?.settings?.deckDesign as DeckDesign);

  const sizeClasses = getSizeClasses(size);

  const overlapClasses = overlapMargins[size];

  const cardClassName = twMerge(
    isButtonCard ? 'w-5 max-w-5' : `${className} max-w-none`,
    highlight && 'highlight',
    isOverlap && `${styles.pokerTableOverlapCard} ${overlapClasses}`,
    'object-cover border-2 border-transparent',
    isWinner && 'border-2 border-ph-yellow',
    isElevated && `${styles.pokerTableCardElevated}`,
    isDiscarded && 'mb-4',
    onClick && 'cursor-pointer',
    sizeClasses,
    'aspect-[60/82]',
    suitColorClass,
    backgroundColorClass,
  );

  return (
    <div
      onClick={onClick}
      style={{
        zIndex: handCardIndex + 1,
        transform: cardAreFanned ? `rotate(${angle}deg) translateY(${translateY}em)` : 'none',
      }}
      className={twMerge(isDiscarded || isElevated ? 'relative -mt-4' : '')}
    >
      {isCardBack ? (
        <div className={twMerge(cardClassName, 'relative flex overflow-hidden', 'bg-transparent', 'border-none')}>
          <CardBack className="h-full w-full flex-1 object-cover" />
        </div>
      ) : (
        <div className={twMerge('relative flex flex-col items-center justify-center', cardClassName)}>
          {/* <Ace/> */}
          <RankSvg className={twMerge('absolute', symbolLayoutClasses, symbolSizeClasses)} />
          {/* Card Symbol Icon */}
          <SuitSvg className={twMerge('absolute', smallSuitClasses, smallSuitSizeClasses)} />
          {/* Small Suit Icon */}
          <SuitSvg className={twMerge('absolute', bigSuitClasses, bigSuitSizeClasses)} />
          {/* Big Suit Icon */}
        </div>
      )}
      {isDiscarded && (
        <div
          className={twMerge(
            'absolute top-4 -mt-4 cursor-pointer bg-black bg-opacity-60',
            isOverlap && overlapClasses,
            sizeClasses,
            'aspect-[60/82]',
          )}
        />
      )}
    </div>
  );
};

export default CardFace;
